import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, Filter, ArrowUp, ArrowDown } from 'lucide-react';
import { userApi, settingsApi } from '../../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export default function UserTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [blcPrice, setBlcPrice] = useState(0.027);

  useEffect(() => {
    settingsApi.get().then(res => {
      setBlcPrice(res.data.settings?.blc_price || 0.027);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await userApi.getTransactions(filter === 'all' ? null : filter);
        setTransactions(response.data.transactions || []);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [filter]);

  const getTypeColor = (type) => {
    switch (type) {
      case 'deposit': return 'text-green-400';
      case 'withdrawal': return 'text-blue-400';
      case 'investment': return 'text-purple-400';
      case 'roi': return 'text-amber-400';
      case 'transfer_in': return 'text-green-400';
      case 'transfer_out': return 'text-red-400';
      case 'admin_adjustment': return 'text-cyan-400';
      default: return 'text-zinc-400';
    }
  };

  const usdToBLC = (usd) => blcPrice > 0 ? (Math.abs(usd) / blcPrice).toFixed(2) : '0.00';

  const getTypeIcon = (type, amount) => {
    if (amount > 0) {
      return <ArrowDown className="w-4 h-4 text-green-400" />;
    }
    return <ArrowUp className="w-4 h-4 text-red-400" />;
  };

  const exportCSV = () => {
    const headers = ['Date', 'Type', 'Amount', 'Status', 'Description'];
    const rows = transactions.map(t => [
      new Date(t.created_at).toLocaleString(),
      t.type,
      t.amount,
      t.status,
      t.description
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transactions.csv';
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="transactions-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-zinc-500/10">
            <FileText className="w-6 h-6 text-zinc-400" />
          </div>
          <div>
            <h1 className="text-2xl text-white font-medium">Transactions</h1>
            <p className="text-sm text-zinc-500">View all your transaction history</p>
          </div>
        </div>

        <button
          onClick={exportCSV}
          className="flex items-center gap-2 px-4 py-2 rounded-lg glass text-zinc-400 hover:text-white"
          data-testid="export-csv"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="w-4 h-4 text-zinc-500" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px] bg-black/50 border-white/10">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all">All Transactions</SelectItem>
            <SelectItem value="deposit">Deposits</SelectItem>
            <SelectItem value="withdrawal">Withdrawals</SelectItem>
            <SelectItem value="investment">Participations</SelectItem>
            <SelectItem value="roi">Daily Rewards</SelectItem>
            <SelectItem value="transfer_in">Transfers In</SelectItem>
            <SelectItem value="transfer_out">Transfers Out</SelectItem>
            <SelectItem value="admin_adjustment">Admin Adjustments</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transactions List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card overflow-hidden"
      >
        {transactions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No transactions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount (USDT)</th>
                  <th>Amount (BLC)</th>
                  <th>Status</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className="text-zinc-400">
                      {new Date(tx.created_at).toLocaleString()}
                    </td>
                    <td>
                      <span className={`capitalize ${getTypeColor(tx.type)}`}>
                        {tx.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="font-medium">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(tx.type, tx.amount)}
                        <span className={tx.amount > 0 ? 'text-green-400' : 'text-red-400'}>
                          {tx.amount > 0 ? '+' : ''}${Math.abs(tx.amount).toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="text-amber-400 font-medium">
                      {tx.amount > 0 ? '+' : '-'}{usdToBLC(tx.amount)} BLC
                    </td>
                    <td>
                      <span className={`badge ${
                        tx.status === 'completed' ? 'badge-success' :
                        tx.status === 'pending' ? 'badge-warning' : 'badge-danger'
                      }`}>
                        {tx.status}
                      </span>
                    </td>
                    <td className="text-zinc-400 text-sm max-w-[200px] truncate">
                      {tx.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
}
